import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Loader, Stack, spacing, Text, Tooltip } from '@scality/core-ui';
import type React from 'react';
import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { LandingPage } from '../../../../gen/openapi';
import { useIsNarrowViewport } from '../../../../hooks/useIsNarrowViewport';
import { Icon } from '../../../Icon';
import {
  SelectChip,
  SelectChipClearButton,
  SelectTriggerButton,
  SelectWrapper,
  selectMenuFloatingPanel,
} from '../../../select';

export type GuardianAgent = {
  id: string;
  name: string;
  description: string;
  icon: string;
  parent: string | null;
  landingPage: LandingPage | null;
  isSelectable: boolean;
  /** The source offers this agent alone: it is preselected and the picker is hidden. */
  isPinned?: boolean;
  children?: GuardianAgent[];
};

const hasAgentChildren = (item: GuardianAgent): boolean => Boolean(item.children && item.children.length > 0);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const menuItemStyles = css`
	display: flex;
	align-items: flex-start;
	gap: ${spacing.r24};
	padding: ${spacing.r8};
	border-radius: 4px;
	outline: none;
	&[data-highlighted] {
		background-color: ${(p) => p.theme.highlight};
	}
`;

const menuPanelStyles = css`
	${selectMenuFloatingPanel}
	padding: ${spacing.r8};
	max-width: min(32rem, calc(100vw - 2rem));
	width: max-content;
	max-height: 20rem;
	overflow-y: auto;
	scrollbar-gutter: stable;
	animation: ${fadeIn} 120ms ease;
`;

const MenuContent = styled(DropdownMenu.Content)`
	${menuPanelStyles}
`;

const SubContent = styled(DropdownMenu.SubContent)`
	${menuPanelStyles}
`;

const MenuItem = styled(DropdownMenu.Item)`
	${menuItemStyles}
	color: ${(p) => p.theme.textPrimary};
`;

const SubTrigger = styled(DropdownMenu.SubTrigger)`
	${menuItemStyles}
	color: ${(p) => p.theme.textPrimary};
	&[data-state='open'] {
		background-color: ${(p) => p.theme.highlight};
	}
`;

const ItemName = styled(Text)`
  min-width: 10rem;
  max-width: 16rem;
`;

const ChevronWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`;

// Inline (narrow) tree pieces: a parent toggle row whose chevron rotates when
// expanded, and children indented beneath it — no side flyout.
const TreeToggle = styled(DropdownMenu.Item)`
	${menuItemStyles}
	color: ${(p) => p.theme.textPrimary};
`;

const TreeChevron = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
  transition: transform 120ms ease;
  transform: rotate(${(p) => (p.$expanded ? '90deg' : '0deg')});
`;

// Children sit behind a vertical rail so the nesting reads as a tree, not just
// an alignment shift. The rail is the inner edge of the indentation.
const IndentedItem = styled(MenuItem)`
  margin-left: ${spacing.r12};
  padding-left: ${spacing.r12};
  border-left: 1px solid ${(p) => p.theme.border};
  border-radius: 0;
`;

const triggerLabelText = (isLoading: boolean, isError: boolean, hasNoItems: boolean): string => {
  if (isLoading) return 'Loading agents...';
  if (isError || hasNoItems) return 'No agents available';
  return 'Select Agent...';
};

export type AgentSelectorProps = {
  items: GuardianAgent[];
  selectedAgent: GuardianAgent | null;
  onAgentSelect: (agent: GuardianAgent | null) => void;
  status: 'pending' | 'success' | 'error';
};

const AgentItem: React.FC<{ agent: GuardianAgent; onSelect: () => void; indented?: boolean }> = ({
  agent,
  onSelect,
  indented = false,
}) => {
  const ItemComponent = indented ? IndentedItem : MenuItem;
  return (
    <ItemComponent
      disabled={!agent.isSelectable}
      onSelect={onSelect}
      style={{ cursor: agent.isSelectable ? 'pointer' : 'default' }}
    >
      <Stack gap="r8">
        <Icon name={agent.icon} />
        <ItemName variant="Small">{agent.name}</ItemName>
      </Stack>
      <Text variant="Small" color="textSecondary">
        {agent.description}
      </Text>
    </ItemComponent>
  );
};

// Narrow-viewport replacement for AgentSubMenu: the parent toggles its children
// inline (a tree) instead of opening a side flyout that would be clipped.
const AgentInlineBranch: React.FC<{
  agent: GuardianAgent;
  expanded: boolean;
  onToggle: () => void;
  onAgentSelect: (agent: GuardianAgent) => void;
}> = ({ agent, expanded, onToggle, onAgentSelect }) => (
  <>
    <TreeToggle
      // Keep the menu open: this row only expands/collapses the branch.
      onSelect={(event) => {
        event.preventDefault();
        onToggle();
      }}
    >
      <Stack gap="r8">
        <Icon name={agent.icon} />
        <ItemName variant="Small">{agent.name}</ItemName>
      </Stack>
      <Text variant="Small" color="textSecondary">
        {agent.description}
      </Text>
      <TreeChevron $expanded={expanded}>
        <Icon name="Arrow-right" size="xs" />
      </TreeChevron>
    </TreeToggle>
    {expanded &&
      agent.children?.map((child) => (
        <AgentItem key={child.id} agent={child} indented onSelect={() => onAgentSelect(child)} />
      ))}
  </>
);

const AgentSubMenu: React.FC<{ agent: GuardianAgent; onAgentSelect: (agent: GuardianAgent) => void }> = ({
  agent,
  onAgentSelect,
}) => (
  <DropdownMenu.Sub>
    <SubTrigger>
      <Stack gap="r8">
        <Icon name={agent.icon} />
        <ItemName variant="Small">{agent.name}</ItemName>
      </Stack>
      <Text variant="Small" color="textSecondary">
        {agent.description}
      </Text>
      <ChevronWrapper>
        <Icon name="Arrow-right" size="xs" />
      </ChevronWrapper>
    </SubTrigger>
    <DropdownMenu.Portal>
      <SubContent sideOffset={4}>
        {agent.children?.map((child) => (
          <AgentItem key={child.id} agent={child} onSelect={() => onAgentSelect(child)} />
        ))}
      </SubContent>
    </DropdownMenu.Portal>
  </DropdownMenu.Sub>
);

export const AgentSelector: React.FC<AgentSelectorProps> = ({ items, selectedAgent, onAgentSelect, status }) => {
  const [open, setOpen] = useState(false);
  const isNarrow = useIsNarrowViewport();
  // Which inline branches are expanded (narrow mode only). Reset when the menu
  // closes so it reopens collapsed.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const hasNoItems = items.length === 0;
  const isDisabled = items.length === 0 || status !== 'success';
  const isLoading = status === 'pending';
  const isError = status === 'error';

  if (selectedAgent) {
    return (
      <SelectWrapper>
        <SelectChip>
          <Stack gap="r8">
            <Icon name={selectedAgent.icon} size="xs" />
            <Text>{selectedAgent.name}</Text>
          </Stack>
          <SelectChipClearButton label={`Stop using agent ${selectedAgent.name}`} onClear={() => onAgentSelect(null)} />
        </SelectChip>
      </SelectWrapper>
    );
  }

  const tooltipOverlay = isLoading
    ? 'Loading agents...'
    : isError
      ? 'An error occurred while loading agents'
      : hasNoItems
        ? 'No agents are available for this deployment'
        : undefined;

  return (
    <SelectWrapper>
      <DropdownMenu.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setExpandedIds(new Set());
        }}
      >
        {isDisabled ? (
          <Tooltip overlay={tooltipOverlay}>
            <SelectTriggerButton
              aria-label={triggerLabelText(isLoading, isError, hasNoItems)}
              aria-disabled
              $isOpen={false}
            >
              <Stack gap="r8">
                {isLoading ? <Loader size="small" /> : <Icon name="Sliders" />}
                <Text>{triggerLabelText(isLoading, isError, hasNoItems)}</Text>
              </Stack>
            </SelectTriggerButton>
          </Tooltip>
        ) : (
          <DropdownMenu.Trigger asChild>
            <SelectTriggerButton aria-label={triggerLabelText(isLoading, isError, hasNoItems)} $isOpen={open}>
              <Stack gap="r8">
                <Icon name="Sliders" />
                <Text>{triggerLabelText(isLoading, isError, hasNoItems)}</Text>
              </Stack>
            </SelectTriggerButton>
          </DropdownMenu.Trigger>
        )}
        <DropdownMenu.Portal>
          <MenuContent side="top" align="start" sideOffset={24}>
            {items.map((item) => {
              if (!hasAgentChildren(item)) {
                return <AgentItem key={item.id} agent={item} onSelect={() => onAgentSelect(item)} />;
              }
              // Narrow viewports expand children inline (no side flyout to clip).
              return isNarrow ? (
                <AgentInlineBranch
                  key={item.id}
                  agent={item}
                  expanded={expandedIds.has(item.id)}
                  onToggle={() => toggleExpanded(item.id)}
                  onAgentSelect={onAgentSelect}
                />
              ) : (
                <AgentSubMenu key={item.id} agent={item} onAgentSelect={onAgentSelect} />
              );
            })}
          </MenuContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </SelectWrapper>
  );
};
